import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { LocaleSwitcher } from './LocaleSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { SoundToggle } from './SoundToggle';
import { Footer } from './Footer';
import { useAuth } from '../hooks/useAuth';

export function Layout() {
  const { t } = useTranslation();
  const { user, isAdmin, signOut } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Scroll-aware compact topnav (threshold 48px, debounced to rAF)
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 48);
        raf = 0;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const linkClass = (isActive: boolean) =>
    `topnav-link${isActive ? ' active' : ''}`;

  return (
    <>
      <a href="#main" className="skip-link">{t('a11y.skipToMain')}</a>
      <nav className={`topnav${scrolled ? ' is-scrolled' : ''}`}>
        <div className="topnav-brand">
          <span className="topnav-brand-mark">BookQuest</span>
          <span className="topnav-brand-sub">{t('brand.sub')}</span>
        </div>

        {/* Desktop links — hidden on mobile */}
        <div className="topnav-links topnav-links-desktop">
          <NavLink to="/library" className={({ isActive }) => linkClass(isActive)}>{t('nav.library')}</NavLink>
          <NavLink to="/upload" className={({ isActive }) => linkClass(isActive)}>{t('nav.upload')}</NavLink>
          <NavLink to="/achievements" className={({ isActive }) => linkClass(isActive)}>{t('nav.achievements')}</NavLink>
          <NavLink to="/vault" className={({ isActive }) => linkClass(isActive)}>{t('nav.vault')}</NavLink>
          <NavLink to="/credentials" className={({ isActive }) => linkClass(isActive)}>{t('nav.credentials')}</NavLink>
        </div>

        <div className="topnav-right">
          <div className="topnav-right-desktop">
            <ThemeToggle />
            <SoundToggle />
            <LocaleSwitcher />
            {user ? (
              <div className="topnav-user-menu">
                {isAdmin && (
                  <NavLink to="/admin" className="topnav-link topnav-admin-link">{t('nav.admin')}</NavLink>
                )}
                <span className="topnav-user-email" title={user.email ?? ''}>
                  {(user.email ?? 'U')[0].toUpperCase()}
                </span>
                <button
                  type="button"
                  className="topnav-signout"
                  onClick={async () => { await signOut(); nav('/login'); }}
                >
                  {t('nav.signout')}
                </button>
              </div>
            ) : (
              <NavLink to="/login" className="topnav-link topnav-signin">{t('nav.signin')}</NavLink>
            )}
          </div>

          {/* Mobile: avatar + hamburger only */}
          <div className="topnav-right-mobile">
            {user && (
              <span className="topnav-user-email" title={user.email ?? ''}>
                {(user.email ?? 'U')[0].toUpperCase()}
              </span>
            )}
            <button
              type="button"
              className="topnav-burger"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {menuOpen && (
        <>
          <div className="mobile-drawer-backdrop" onClick={() => setMenuOpen(false)} />
          <aside className="mobile-drawer" role="dialog" aria-label="Navigation menu">
            <nav className="mobile-drawer-links">
              <NavLink to="/library" className={({ isActive }) => `mobile-drawer-link${isActive ? ' active' : ''}`}>{t('nav.library')}</NavLink>
              <NavLink to="/upload" className={({ isActive }) => `mobile-drawer-link${isActive ? ' active' : ''}`}>{t('nav.upload')}</NavLink>
              <NavLink to="/achievements" className={({ isActive }) => `mobile-drawer-link${isActive ? ' active' : ''}`}>{t('nav.achievements')}</NavLink>
              <NavLink to="/vault" className={({ isActive }) => `mobile-drawer-link${isActive ? ' active' : ''}`}>{t('nav.vault')}</NavLink>
              <NavLink to="/credentials" className={({ isActive }) => `mobile-drawer-link${isActive ? ' active' : ''}`}>{t('nav.credentials')}</NavLink>
              {isAdmin && (
                <NavLink to="/admin" className={({ isActive }) => `mobile-drawer-link${isActive ? ' active' : ''}`}>{t('nav.admin')}</NavLink>
              )}
            </nav>
            <div className="mobile-drawer-foot">
              <ThemeToggle />
              <SoundToggle />
              <LocaleSwitcher />
              {user ? (
                <button
                  type="button"
                  className="topnav-signout mobile-drawer-signout"
                  onClick={async () => { await signOut(); nav('/login'); setMenuOpen(false); }}
                >
                  {t('nav.signout')}
                </button>
              ) : (
                <NavLink to="/login" className="topnav-link topnav-signin">{t('nav.signin')}</NavLink>
              )}
            </div>
          </aside>
        </>
      )}

      <div id="main" tabIndex={-1}>
        <div key={location.pathname} className="bq-route-enter">
          <Outlet />
        </div>
      </div>
      <Footer />
    </>
  );
}
